import * as Sentry from '@sentry/tanstackstart-react'
import { QueryClient } from '@tanstack/react-query'
import { createRouter as createTanStackRouter } from '@tanstack/react-router'
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query'
import { routeTree } from './routeTree.gen'
import { setAuthQueryClient } from './lib/auth-client'
import { ApiError } from './lib/api'
import { STALE_TIMES } from './lib/query-config'

export function getRouter() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        staleTime: STALE_TIMES.lists,
        gcTime: STALE_TIMES.details,
        retry: 1,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      },
    },
  })

  const router = createTanStackRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
  })

  if (!router.isServer) {
    const isWKWebView = /\bCriOS\b|WKWebView|FxiOS/.test(navigator.userAgent)
      || (navigator.userAgent.includes('AppleWebKit') && !navigator.userAgent.includes('Safari'))

    try {
      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE ?? 'development',
        release: import.meta.env.VITE_SENTRY_RELEASE,
        integrations: isWKWebView
          ? []
          : [Sentry.browserTracingIntegration()],
        enableLogs: true,
        beforeSend(event, hint) {
          // Drop expected 4xx ApiError events (matches backend convention:
          // "4xx errors are expected — only capture 5xx"). 5xx ApiErrors and
          // any non-ApiError exception still flow through.
          const exception = hint?.originalException
          if (exception instanceof ApiError && exception.status >= 400 && exception.status < 500) {
            return null
          }
          return event
        },
        beforeSendLog(log) {
          if (log.level === 'debug') return null
          if (log.attributes) {
            for (const key of ['password', 'token', 'cookie', 'authorization', 'secret']) {
              delete log.attributes[key]
            }
          }
          return log
        },
        tracesSampleRate: import.meta.env.PROD ? 0.1 : 0,
        sendDefaultPii: false,
      })
    } catch {
      // browserTracingIntegration can throw in WKWebView (WeakMap key error)
    }
  }

  setupRouterSsrQueryIntegration({ router, queryClient })
  setAuthQueryClient(queryClient)

  return router
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>
  }
}
